/* Copyright (c) 2021 Caring Transitions Online Auctions. All Rights Reserved. */

import { Component, useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Form, Spinner, Button } from 'react-bootstrap';
import { Helmet } from 'react-helmet';

import { CityAndStateService } from 'shared/services';
import validators from 'shared/validators';
import { ArrayValueReorder } from 'shared/modifiers';
import { LocationListHoc } from '../HOC/LocationListHoc';

import './LocationsList.scss';
import { Breadcrumbs } from 'shared/hoc/breadcrumbs/Breadcrumbs';
import { BREADCRUMB_TYPE_CONFIG } from 'shared/hoc/breadcrumbs/config';
import { Loader } from 'shared/hoc/loader/Loader';

/**

@Author Karthik

@param

@return html code for locations list

**/
let pageSize = 24;

const LocationsList = (props) => {

    // Initial form structure
    const initialLocationFilterForm = {
        name: { value: 'name-a-z', error: '', validators: [validators.required()] },
        location: { value: 'All', error: '', validators: [validators.required()] },
        city: { value: 'All', error: '', validators: [validators.required()] },
        zipcode: { value: '', error: '', validators: [validators.equalToLength(5)] },
        loctitle: { value: '', error: '', validators: [validators.required()] },
        miles: { value: '5', error: '', validators: [validators.required()] }
    };

    // States
    const [locationFilterForm, setLocationFilterForm] = useState({ ...initialLocationFilterForm });
    const [saleCount, setSaleCount] = useState(false);

    const [filterLoading, setFilterLoading] = useState(false);
    const dispatch = useDispatch()
    const statemaintenance = useSelector(state => state?.statemaintenance);

    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const [nextList, setNextList] = useState(null);

    const [formValid, setFormValid] = useState(true);

    const [payloadData, setPayloadData] = useState(statemaintenance?.["Estate-Sale-List"]?.searchkey || null);
    const [pageLoad, setpageLoad] = useState(true)

    useEffect(() => {
        setpageLoad(true)
        getStateList();
        if (statemaintenance?.["Location-List"]) {
            let data = statemaintenance?.["Location-List"]
            setLocationFilterForm(data?.forminput || locationFilterForm);
            setPayloadData({ ...data?.searchkey } || payloadData)
            let pusttoStore = { ...statemaintenance }
            dispatch({
                type: 'PUSH_TO_STATE', payload: pusttoStore
            });
        }
        setpageLoad(false)
    }, []);

    const onInputValidation = (event, formName = "locationFilterForm") => {
        const { name, value } = event.target;
        let controlError = '';

        const currentForm = formName === "locationFilterForm" ? locationFilterForm : {};

        // Run all validators
        for (let validator of currentForm[name].validators) {
            controlError = validator(value);
            if (controlError) break;
        }

        let updatedValue = value;

        // Validate Zipcode
        if (name === 'zipcode' && isNaN(Number(value))) {
            updatedValue = '';
        }

        // Update control state
        const updatedControl = {
            ...currentForm[name],
            value: updatedValue,
            valid: !Boolean(controlError),
            error: controlError
        };

        const updatedForm = {
            ...currentForm,
            [name]: updatedControl
        };

        // Handle special case for location change
        if (name === "location") {
            setCityList([]);

            updatedForm["city"] = {
                ...updatedForm["city"],
                value: "All",
                valid: false,
                error: ""
            };

            if (updatedValue !== "All") {
                getCityList(event);
            }
        }
        // Set updated form
        if (formName === "locationFilterForm") {
            setLocationFilterForm(updatedForm);
        }

        // Validate entire form
        const formIsValid = Object.keys(updatedForm).every(
            key => updatedForm[key].valid !== false
        );

        setFormValid(formIsValid);
    };

    const getStateList = () => {
        CityAndStateService.getStateList().then(stateList => {
            const reorderedStates = ArrayValueReorder(stateList?.data?.data || [], 'state');
            setStateList(reorderedStates);
        });
    }
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            filterLocation()
        }
    }

    const getCityList = (event) => {
        CityAndStateService.getCityList(event.target.value).then(
            cityList => {
                setCityList(ArrayValueReorder(cityList.data.data, 'city'));
            }
        ).catch(error => error);
    }

    const filterLocation = async (append = false, data) => {
        const value = data ? data : locationFilterForm;

        const payload = {
            sort: [{ field: "title", direction: "asc" }],
            page: nextList && append ? { size: pageSize, next: nextList } : { size: pageSize },
            field: [
                "id", "title", "contactname", "email", "salecount", "phone",
                "imageurl", "locationstatecode", "locationcode", "website", "state", "city"
            ]
        };

        const filter = [];
        if (value.location.value !== 'All') {
            filter.push({ field: "state", value: value.location.value, op: "=", join: "AND" });
        }

        if (value.city.value !== 'All') {
            filter.push({ field: "city", value: value.city.value, op: "=", join: "AND" });
        }

        if (value.zipcode.value) {
            filter.push({ field: "zip", value: value.zipcode.value, op: "=", join: "AND" });
        }

        if (value.zipcode.value || (value.location.value !== 'All' && value.city.value !== 'All')) {
            filter.push({ field: "miles", value: value.miles.value, op: "=", join: "AND" });
        }

        if (value.loctitle.value) {
            filter.push({ field: "title", value: `%${value.loctitle.value}%`, op: "LIKE", join: "AND" });
        }

        if (saleCount) {
            filter.push({ field: "salecount", value: 0, op: ">", join: "AND" });
        }

        if (filter.length > 0) {
            payload.filter = filter;
        }

        if (value.name.value) {
            const sortOptions = [
                { name: 'name-a-z', field: 'title', direction: 'asc' },
                { name: 'name-z-a', field: 'title', direction: 'desc' },
                { name: 'seller-name-a-z', field: 'contactname', direction: 'asc' },
                { name: 'seller-name-z-a', field: 'contactname', direction: 'desc' }
            ];
            const selected = sortOptions.find(x => x.name === value.name.value);
            if (selected) {
                payload.sort = [{ field: selected.field, direction: selected.direction }];
            }
        }
         let pusttoStore = { ...statemaintenance }
        pusttoStore["Location-List"] = {
            forminput:locationFilterForm,
            searchkey:payload
        }
        dispatch({
            type: 'PUSH_TO_STATE', payload: pusttoStore
        });
        setPayloadData(payload);   
    };

    const clearSearch = () => {
        let pusttoStore = { ...statemaintenance }
        delete pusttoStore["Location-List"] 
        dispatch({
            type: 'PUSH_TO_STATE', payload: pusttoStore
        });
        setSaleCount(false);
        setNextList(null);
        setCityList([]);
        setLocationFilterForm({ ...initialLocationFilterForm });
        setPayloadData(null);
    };

    const back = () => {
        props.history.push("/");
    }

    const onCheckboxChange = (e) => {
        setSaleCount(e.target.checked ? true : false);
    }
    if (pageLoad) {
        return (
            <Loader/>
        )
    }

    return (
        <div className="locations-list-main" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #E9ECEF 31%, #F1F3F5 100%)", flex: '1', }}>
            <Helmet>
                <title>CTBids | Locations</title>
            </Helmet>
            <div className="locations-list">
                <Breadcrumbs props={props} type={BREADCRUMB_TYPE_CONFIG.LOCATIONS} />
                <div className="top-header h5">
                    <div className={`d-flex flex-column align-items-center location-resp`} style={{ width: "70%" }}>
                        <div className="loc-large-screen-width d-flex align-items-center justify-content-center font-weight-bold head-resp" style={{ color: "#00263E", fontSize: "32px", lineHeight:"48px" }}>
                            Search estate sales by locations
                        </div>
                        <div className='d-flex align-items-center justify-content-center text-center subhead-resp' style={{ color: "#00263E", fontSize: "18px" }}>
                            Go through the variety of Estate Sales to find treasures behind the closed doors. Search easily through different listings in your location and place your bids. Favorite your preferred locations for the future.
                        </div>
                    </div>
                </div>
                <div className="filter-section">
                    <div className={`d-flex align-items-center justify-content-center`}>
                        <div className="loc-large-screen-width">
                            <div className="d-lg-flex align-items-start justify-content-between" style={{ fontSize: "12px", lineHeight: "16px" }}>
                                <div className='w-100 loc-field-resp'>
                                    <div className="title font-weight-bold label-wd">Location Name</div>
                                    <div className="items-select d-flex align-items-center input-wd">
                                        <Form.Group controlId="loctitle" className='d-flex w-100 input-border'>
                                            <Form.Control className='custom-location-input' type="text" placeholder="Search" aria-label="Search" name="loctitle" value={locationFilterForm.loctitle.value} onKeyDown={handleKeyDown} onChange={onInputValidation} />
                                        </Form.Group>
                                    </div>
                                    {
                                        locationFilterForm.loctitle.value && locationFilterForm.loctitle.error ? <small className="text-danger">{locationFilterForm.loctitle.error}</small> : null
                                    }
                                </div>
                                <div className='w-100 loc-field-resp'>
                                    <div className="title font-weight-bold label-wd">State</div>
                                    <div className="items-select d-flex align-items-center input-wd" style={{ fontWeight: "bolder" }}>
                                        <Form.Group controlId="Items.StateControlSelect" className='w-100 d-flex align-items-center input-border'>
                                            <Form.Select  className='custom-location-input' as="select" name="location" aria-label="Select location" onChange={onInputValidation} value={locationFilterForm.location.value} style={{ color: "#00263E", border: "none !important" }}>
                                                <option selected hidden style={{ fontWeight: "bolder" }}>Select State</option>
                                                <option value="All">All</option>
                                                {
                                                    stateList.map((state, index) => <option key={`state_${index}`} value={state.state}>{state.state}</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className='w-100 loc-field-resp'>
                                    <div className="title font-weight-bold label-wd">City</div>
                                    <div className="items-select d-flex align-items-center input-wd" style={{ fontWeight: "bolder" }}>
                                        <Form.Group controlId="Items.CityControlSelect" className='w-100 d-flex align-items-center input-border'>
                                            <Form.Select  as="select" name="city" aria-label="Select city" className="city-in custom-location-input" onChange={onInputValidation} value={locationFilterForm.city.value} style={{ color: "#00263E" }}>
                                                <option selected hidden>Select City</option>
                                                <option value="All">All</option>
                                                {
                                                    cityList.map((city, index) => <option key={`city_${index}`} value={city.city}>{city.city}</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className='w-100 loc-field-resp'>
                                    <div className="title font-weight-bold label-wd">Zip Code</div>
                                    <div className="items-select d-flex align-items-center input-wd" style={{ backgroundColor: "#F8F9FA" }}>
                                        <Form.Group controlId="zipcode" className='w-100 input-border'>
                                            <Form.Control  className='custom-location-input' type="text" aria-label="Enter zip code" placeholder="Enter Zip code" name="zipcode" value={locationFilterForm.zipcode.value} onChange={onInputValidation} style={{ color: "#00263E" }} />
                                        </Form.Group>
                                    </div>
                                    {
                                        locationFilterForm.zipcode.value && locationFilterForm.zipcode.error ? <small className="text-danger">{locationFilterForm.zipcode.error}</small> : null
                                    }
                                </div>
                                <div className='w-100 loc-field-resp'>
                                    <div className="title font-weight-bold label-wd">Within the radius of</div>
                                    <div className="items-select d-flex align-items-center input-wd" style={{ fontWeight: "bolder" }}>
                                        <Form.Group controlId="Items.MilesControlSelect" className='w-100 d-flex align-items-center input-border'>
                                            <Form.Select  as="select" name="miles" aria-label="Select miles" className="miles-in custom-location-input" onChange={onInputValidation} value={locationFilterForm.miles.value} style={{ color: "#00263E" }}>
                                                <option selected hidden>Select Distance</option>
                                                <option value="5">5 Miles</option>
                                                <option value="10">10 Miles</option>
                                                <option value="25">25 Miles</option>
                                                <option value="50">50 Miles</option>
                                                <option value="100">100 Miles</option>
                                                <option value="250">250 Miles</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className='w-100 loc-field-resp'>
                                    <div className="title font-weight-bold label-wd">Sort by</div>
                                    <div className="sort-by d-flex align-items-center input-wd" style={{ fontWeight: "bolder" }}>
                                        <Form.Group  controlId="Sort.LocationControlSelect" className='w-100 d-flex align-items-center input-border'>
                                            <Form.Select  className='custom-location-input' as="select" name="name" aria-label="Select location" onChange={onInputValidation} value={locationFilterForm.name.value}>
                                                <option value="name-a-z">Location Name A-Z</option>
                                                <option value="name-z-a">Location Name Z-A</option>
                                                <option value="seller-name-a-z">Seller Name A-Z</option>
                                                <option value="seller-name-z-a">Seller Name Z-A</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                </div>
                            </div>
                            <div className='d-flex align-items-center justify-content-between'>
                                    <div className="checkbox-ml-10">
                                    <Form.Group className="multiple-checkbox checkbox-bottom font-weight-bold">
                                        <Form.Check custom type="checkbox" id="saleCount" name="saleCount" label="Show locations with Active Sales" checked={saleCount} onChange={(e) => { onCheckboxChange(e) }} />
                                    </Form.Group>
                                </div>

                                <div className="search-button d-flex justify-content-center align-items-center mr-10" style={{ marginTop: "20px" }}>
                                    <div className="mx-3">
                                        <div className="clickable d-flex" onClick={clearSearch} style={{ background: "var(--Background-Gray-light, #868E961A)", padding: "10px 15px", borderRadius: "5px", fontWeight: "bold" }}>Reset</div>
                                    </div>
                                    <Button className='location-search d-flex' variant="secondary" onClick={filterLocation}>Search</Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <LocationListHoc props={props} search={payloadData}  pageSetCall={(index)=>{
                    
                }}/>
            </div>
        </div>
    );
}

export default LocationsList;
